import ConnectedAccountListOptions from '@/components/ConnectedAccount/ConnectedAccountListOptions.vue'
import ConnectedAccountStatusText from '@/components/ConnectedAccount/ConnectedAccountStatusText.vue'
import { Banknote, Building2, Clock, HandCoins, Landmark, Store } from 'lucide-vue-next'
import MoneyFormatted from 'pejer/src/components/MoneyFormatted.vue'
import { Badge } from 'pejer/src/components/ui/badge'
import { Groups, JSSchema } from 'pejer/src/tables'
import { MoneyFormatter } from 'pejer/src/tables/Global'
import { ColorPalette } from 'pejer/src/tables/helpers'
import { h } from 'vue'

export namespace ConnectedAccountsEmbed {
  export interface Full extends ListItem {}

  export interface ListItem extends ConnectedAccountV2 {}
}

export interface ConnectedAccountV2 {
  connectedAccountID: string // Guid
  platformId: string // Guid
  tenantId: string
  externalReferenceId: string
  aliasName: string
  currency: string
  countryCode: string
  organizationId: string
  businessName: string
  payoutRecipientEmail: string
  businessType: string
  payoutsEnabled: boolean
  bban: string
  iban: string
  website: string
  purposeDescription: string
  createdAt: string
  updatedAt: string | null
  tosAcceptedAt: string | null
  signatureCompletedAt: string | null
  partialPayAcceptedAt: string | null
  currentBalance: number | null
  awaitingNetsOCRAgreement: boolean
  netsOcrAgreementOptionActivated: boolean
  isArchived: boolean
  statusInfo: string
  directPaymentAccountStatus:
    | 'NotApplicable'
    | 'AwaitingReadConfirmation'
    | 'Active'
    | 'Deactivated'
  isDirectPaymentAccount: boolean

  individualFirstname: string
  individualLastname: string
  individualEmail: string
  individualCity: string
  individualZipcode: string
}

export const ConnectedAccountEmbedGroups = {
  Bedrift: {
    icon: Building2,
    color: ColorPalette.red,
    mainColumn: 'businessName',
  },
  Konto: {
    icon: Store,
    color: ColorPalette.pink,
    isMainGroup: true,
    mainColumn: 'aliasName',
  },
  Balanse: {
    icon: Banknote,
    color: ColorPalette.green,
    mainColumn: 'currentBalance',
  },
  Status: {
    icon: HandCoins,
    color: ColorPalette.brown,
    mainColumn: 'statusInfo',
  },
  Time: {
    icon: Clock,
    color: ColorPalette.black,
    mainColumn: 'createdAt',
  },
} satisfies Groups

export const ConnectedAccountEmbedListSchema: JSSchema<
  ConnectedAccountsEmbed.ListItem,
  typeof ConnectedAccountEmbedGroups
> = {
  name: 'Connected Accounts Embed',
  title: 'Connected Accounts',
  groups: ConnectedAccountEmbedGroups,
  fields: {
    businessName: {
      type: 'string',
      humanReadable: 'Bedriftsnavn',
      smartSearchType: 'Lax',
      meta: {
        group: 'Bedrift',
        isMain: true,
      },
      // formattedCell: (value: string) => textWithIconCell(Building2, value),
      sensitive: true,
    },
    payoutRecipientEmail: {
      type: 'string',
      humanReadable: 'Epost for bilag',
      smartSearchType: 'Lax',
      metaType: 'email',
      meta: {
        group: 'Konto',
      },
      sensitive: true,
    },
    signatureCompletedAt: {
      type: 'date',
      humanReadable: 'Signert',
      meta: {
        group: 'Bedrift',
        hideFromTable: true,
      },
    },
    partialPayAcceptedAt: {
      type: 'date',
      humanReadable: 'Delbetaling akseptert',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
    },
    payoutsEnabled: {
      type: 'boolean',
      humanReadable: 'Utbetaling godkjent',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
    },
    tosAcceptedAt: {
      type: 'date',
      humanReadable: 'Vilkår akseptert',
      meta: {
        hideFromTable: true,
        group: 'Bedrift',
      },
    },
    aliasName: {
      type: 'string',
      humanReadable: 'Alias navn',
      smartSearchType: 'Lax',
      meta: {
        group: 'Konto',
        isMain: true,
      },
      // formattedCell: (value) => textWithIconCell(Store, value),
      specificCell:
        (self) =>
        ({ row }) => {
          return h('div', { class: 'flex gap-2' }, [
            row.original.aliasName,
            row.original.isDirectPaymentAccount
              ? h(Badge, { variant: 'secondary', class: 'text-xs gap-1' }, [
                  h(Landmark, { size: 12 }),
                  'Direktekonto',
                ])
              : null,
          ])
        },
      sensitive: true,
      inSearchSelect: true,
    },
    connectedAccountID: {
      type: 'string',
      humanReadable: 'Connected Account ID',
      primaryKey: true,
      meta: {
        group: 'Konto',
        hideFromTable: true,
      },
      // formattedCell(value: string) {
      //   return h(
      //     ColumnLink,
      //     {
      //       content: value,
      //       id: value,
      //       pathName: 'account',
      //     },
      //     () => value
      //   )
      // },
    },
    isDirectPaymentAccount: {
      type: 'boolean',
      humanReadable: 'Direkte betalingskonto',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
    },
    directPaymentAccountStatus: {
      type: 'string',
      humanReadable: 'Status for direkte betalingskonto',
      meta: {
        group: 'Konto',
      },
    },
    currentBalance: {
      type: 'number',
      humanReadable: 'Balanse',
      formatter: MoneyFormatter,
      metaType: 'money',
      meta: {
        group: 'Balanse',
        isMain: true,
      },
      specificCell:
        () =>
        ({ row }) => {
          return h(MoneyFormatted, {
            amount: row.original.currentBalance,
            oneSize: true,
            currency: 'NOK',
            showColor: true,
          })
        },
    },
    bban: {
      type: 'string',
      humanReadable: 'Kontonummer',
      meta: {
        group: 'Konto',
      },
      sensitive: true,
      inSearchSelect: true,
    },
    countryCode: {
      type: 'string',
      humanReadable: 'Landkode',
      meta: {
        group: 'Bedrift',
      },
      metaType: 'countryCode',
    },
    organizationId: {
      type: 'string',
      humanReadable: 'Organisasjonsnummer',
      meta: {
        group: 'Bedrift',
      },
      sensitive: true,
    },
    awaitingNetsOCRAgreement: {
      type: 'boolean',
      humanReadable: 'Venter på OCR avtale',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
      metaType: 'YellowYesGreenNo',
    },
    netsOcrAgreementOptionActivated: {
      type: 'boolean',
      humanReadable: 'Har mulighet for OCR avtale',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
    },
    statusInfo: {
      type: 'string',
      humanReadable: 'Status',
      smartSearchType: 'Lax',
      meta: {
        group: 'Status',
        isMain: true,
      },
      sensitive: true,
      specificCell:
        () =>
        ({ row }) => {
          return h(ConnectedAccountStatusText, {
            connectedAccount: row.original,
          })
        },
    },
    isArchived: {
      type: 'boolean',
      humanReadable: 'Arkivert',
      meta: {
        hideFromTable: true,
        group: 'Konto',
      },
      metaType: 'RedYesGreenNo',
    },
    createdAt: {
      type: 'date',
      humanReadable: 'Laget',
      meta: {
        group: 'Time',
      },
      isDefaultSortColumn: true,
    },
  },
  actions: {
    options: {
      cell: ({ row, emits }) => {
        return h(ConnectedAccountListOptions, {
          connectedAccount: row.original,
          'onUpdate:close': emits.reload,
        })
      },
    },
  },
}

export const ConnectedAccountEmbedFullSchema: JSSchema<
  ConnectedAccountsEmbed.Full,
  typeof ConnectedAccountEmbedGroups
> = {
  ...ConnectedAccountEmbedListSchema,
  title: 'Connected Account',
}
